<template>
	<div class="input-wrapper">
		<label class="label form-label">
			{{ textType.labelText }}
		</label>
		<div class="sfc-fields">
			<input
				ref="fieldOne"
				v-model.trim="firstInput"
				class="input form-control sfc-field-1"
				:class="{
					'input--error': v$.fieldInput.$error,
				}"
				maxLength="2"
				type="text"
				:placeholder="textType.placeholder.firstInput"
				:autocomplete="textType.autocomplete?.firstInput"
				@blur="firstInputDirty = true"
				@input="handleInput"
				@keydown="handleKeyDown"
				@keyup="focusNextInput('first', $event)"
			>
			<input
				ref="fieldTwo"
				v-model.trim="secondInput"
				class="input form-control sfc-field-2"
				:class="{
					'input--error': v$.fieldInput.$error,
				}"
				maxLength="2"
				type="text"
				:placeholder="textType.placeholder.secondInput"
				:autocomplete="textType.autocomplete?.secondInput"
				@blur="secondInputDirty = true"
				@input="handleInput"
				@keydown="handleKeyDown"
				@keyup="focusNextInput('second', $event)"
			>
			<input
				ref="fieldThree"
				v-model.trim="thirdInput"
				class="input form-control sfc-field-3"
				:class="{
					'input--error': v$.fieldInput.$error,
				}"
				:maxLength="thirdInputMaxDigits"
				type="text"
				:placeholder="textType.placeholder.thirdInput"
				:autocomplete="textType.autocomplete?.thirdInput"
				@blur="thirdInputDirty = true"
				@input="handleInput"
				@keydown="handleKeyDown"
			>
		</div>
		<p
			v-if="v$.fieldInput.$error"
			class="error-text"
		>
			{{ textType.errorText }}
		</p>
	</div>
</template>
<script>
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';

import { intervalToDuration, parse, isValid } from 'date-fns';

export default {
	props: {
		inputType: {
			type: String,
			default: 'dob',
		},
		errorMessage: {
			type: String,
			default: '',
		},
		labelText: {
			type: String,
			default: ''
		},
		errorText: {
			type: String,
			default: ''
		},
		prefillInput: {
			type: String,
			default: '',
			required: false,
		},
	},
	emits: ['field-input'],
	data() {
		return {
			v$: useVuelidate(),
			firstInput: '',
			secondInput: '',
			thirdInput: '',
			firstInputDirty: false,
			secondInputDirty: false,
			thirdInputDirty: false,
		};
	},
	computed: {
		thirdInputMaxDigits() {
			switch (this.inputType) {
				case 'sortCode':
					return 2;
				default:
					return 4;
			}
		},
		textType() {
			switch (this.inputType) {
				case 'sortCode':
					return {
						placeholder: {
							firstInput: '',
							secondInput: '',
							thirdInput: '',
						},
						labelText: this.labelText,
						errorText: 'Please enter a valid 6 digit sort code',
					};
				default: {
					return {
						placeholder: {
							firstInput: 'DD',
							secondInput: 'MM',
							thirdInput: 'YYYY',
						},
						labelText: this.labelText,
						errorText: this.errorText,
						autocomplete: {
							firstInput: 'bday-day',
							secondInput: 'bday-month',
							thirdInput: 'bday-year',
						},
					};
				}
			}
		},
		fieldInput() {
			// Pad single digits with "0"
			const paddedFirstInput = this.firstInput.length === 1 ? `0${this.firstInput}` : this.firstInput;
			const paddedSecondInput = this.secondInput.length === 1 ? `0${this.secondInput}` : this.secondInput;
			if (this.inputType === 'sortCode') {
				return paddedFirstInput + paddedSecondInput + this.thirdInput;
			}
			else {
				return `${paddedFirstInput}-${paddedSecondInput}-${this.thirdInput}`;
			}
		},
	},
	watch: {
		firstInputDirty() {
			this.v$.fieldInput.$dirty = (this.firstInputDirty && this.secondInputDirty && this.thirdInputDirty);
		},
		secondInputDirty() {
			this.v$.fieldInput.$dirty = (this.firstInputDirty && this.secondInputDirty && this.thirdInputDirty);
		},
		thirdInputDirty() {
			this.v$.fieldInput.$dirty = (this.firstInputDirty && this.secondInputDirty && this.thirdInputDirty);
		},
	},
	methods: {
		handleKeyDown(event) {
			if (
				['Backspace', 'Delete', 'Tab'].includes(event.key)
				|| [37, 39, 35].includes(event.keyCode) || (event.key >= 0 && event.key <= 9)
			) { }
			else {
				event.preventDefault();
			}
		},
		handleInput() {
			this.$emit('field-input', {
				value: this.fieldInput,
				isValid: (!this.v$.fieldInput.$invalid && this.fieldInput.length > 0),
			});
		},
		calculateAge() {
			const todaysDate = new Date();

			const birthDate = parse(this.fieldInput, 'dd-MM-yyyy', new Date());
			let age = intervalToDuration({
				start: birthDate,
				end: todaysDate,
			}).years;
			if (age > 100 || age < 0) {
				return false;
			}
			return true;
		},
		focusNextInput(name, event) {
			if (
				event.key === 'Tab'
				|| event.key === 'Shift'
			) {
				return;
			}

			let
				current,
				next;

			switch (name) {
				case 'first':
					current = this.$refs.fieldOne;
					next = this.$refs.fieldTwo;
					break;
				case 'second':
					current = this.$refs.fieldTwo;
					next = this.$refs.fieldThree;
					break;
			}

			if (current.value.length < 2) {
				return;
			}

			next.focus();
		},
	},
	mounted() {
		if (this.prefillInput) {
			this.firstInput = this.prefillInput.slice(0, 2);
			this.secondInput = this.prefillInput.slice(2, 4);
			this.thirdInput = this.prefillInput.slice(4, 6);
		}
	},
	validations() {
		switch (this.inputType) {
			case 'sortCode':
				return {
					fieldInput: {
						required,
						minLength: minLength(6),
					},
				};
			default:
				return {
					fieldInput: {
						required: true,
						isValidDate: (value) => {
							if (value.length >= 3) {
								const parsedDate = parse(value, 'dd-MM-yyyy', new Date());
								return isValid(parsedDate);
							}
						},
						isValidAge: () => {
							return this.calculateAge();
						},
					},
				};
		}
	},
};
</script>

<style scoped lang="scss">

.sfc-fields {
	display: flex;
	gap: 15px;
}

.sfc-field-1,
.sfc-field-2 {
	max-width: 59px !important; // override .input-wrapper input[type=text]
}

.sfc-field-3 {
	max-width: 70px !important; // override .input-wrapper input[type=text]
}

</style>
